// 共用 UI 元件
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api, money } from './api.js';
import Icon from './Icons.jsx';

/* ---------- Toast ---------- */
const ToastCtx = createContext({ toast: () => {} });
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }) {
  const [list, setList] = useState([]);
  const toast = useCallback((msg, type = 'ok') => {
    const id = Date.now() + Math.random();
    setList(l => [...l, { id, msg, type }]);
    setTimeout(() => setList(l => l.filter(t => t.id !== id)), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={{ toast }}>
      {children}
      <div className="toast-wrap">
        {list.map(t => (
          <div key={t.id} className={`toast ${t.type}`}>
            <span>{t.type === 'ok' ? '✅' : t.type === 'err' ? '⚠️' : 'ℹ️'}</span>
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ---------- Modal ---------- */
export function Modal({ title, icon, children, footer, onClose, size }) {
  return (
    <div className="modal-mask" onMouseDown={e => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div className={`modal ${size || ''}`}>
        <div className="modal-head">
          {icon ? <span className="ico-badge">{icon}</span> : null}
          <div className="modal-title">{title}</div>
          {onClose && <button className="btn sm" onClick={onClose}>✕ 關閉</button>}
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ---------- 確認對話 ---------- */
export function useConfirm() {
  const [state, setState] = useState(null);
  const confirm = (message, action, opts = {}) => setState({ message, action, ...opts });
  const node = state ? (
    <Modal title={state.title || '確認操作'} icon="❓" size="" onClose={() => setState(null)}
      footer={<>
        <button className="btn" onClick={() => setState(null)}>取消</button>
        <button className={`btn ${state.danger ? 'coral' : 'primary'}`} onClick={async () => {
          try { await state.action(); setState(null); } catch (e) { setState(null); }
        }}>{state.okLabel || '確定'}</button>
      </>}>
      <p style={{ lineHeight: 1.7, color: 'var(--ink-2)' }}>{state.message}</p>
    </Modal>
  ) : null;
  return [confirm, node];
}

/* ---------- 欄位 ---------- */
export function Field({ label, required, children, span2 }) {
  return (
    <div className={`field ${span2 ? 'span2' : ''}`}>
      {label ? <label>{label}{required ? <span className="req"> *</span> : null}</label> : null}
      {children}
    </div>
  );
}

/* ---------- 狀態徽章 ---------- */
export function StatusBadge({ map, value }) {
  const s = map[value] || { label: value, cls: 'st-gray' };
  return <span className={`st ${s.cls}`}>{s.label}</span>;
}

export function CategoryTag({ value }) {
  const c = { INSTALLATION: 'cat-ins', MAINTENANCE: 'cat-mnt', ECALL: 'cat-ecl', PROCUREMENT: 'cat-mnt' }[value] || 'cat-ins';
  const label = { INSTALLATION: '🔧 安裝工程', MAINTENANCE: '🛡️ 保養服務', ECALL: '🚨 緊急維修', PROCUREMENT: '📦 採購' }[value] || value;
  return <span className={`cat-tag ${c}`}>{label}</span>;
}

/* ---------- 分頁 (資料載入) ---------- */
export function Loading() { return <div className="loading">載入中…</div>; }
export function Empty({ icon = '📭', text, children }) {
  return (
    <div className="empty">
      <div className="ball">{icon}</div>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{text}</div>
      {children}
    </div>
  );
}

/* ---------- 頁首 ---------- */
export function PageHead({ icon, iconCls, title, desc, actions }) {
  return (
    <div className="page-head">
      <div>
        <div className="page-title">
          <span className={`ico-badge ${iconCls || ''}`}><Icon name={icon} /></span>
          {title}
        </div>
        {desc ? <div className="page-desc">{desc}</div> : null}
      </div>
      {actions ? <div className="head-actions">{actions}</div> : null}
    </div>
  );
}

/* ---------- 選擇客戶後彈出地址選單 (必須揀或新增, 唔自動 default) ---------- */
export function AddressPicker({ customerId, addresses, value, onChange, onNewAddress }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ label: '', address: '' });
  const { toast } = useToast();
  const saveNew = async () => {
    if (!f.address.trim()) { toast('請填寫地址', 'err'); return; }
    try {
      const a = await api.post('/customers/' + customerId + '/addresses', f);
      toast('新地址已加入客戶 📍');
      setAdding(false); setF({ label: '', address: '' });
      if (onNewAddress) onNewAddress(a);
      onChange(a.id);
    } catch (e) { toast(e.message, 'err'); }
  };
  return (
    <div className="field">
      <label>地盤 / 施工地址 (Site — 可選){value ? null : <span style={{ color: 'var(--amber)' }}> — 如需要先揀</span>}</label>
      <div className="pill-filter">
        {addresses.map(a => (
          <button key={a.id} type="button" className={`pill ${value === a.id ? 'active' : ''}`}
            onClick={() => onChange(a.id)}>
            📍 {a.label || '地址'}{a.is_default ? ' ⭐' : ''}
          </button>
        ))}
        <button type="button" className={`pill ${adding ? 'active' : ''}`} onClick={() => setAdding(x => !x)}>➕ 新地址</button>
      </div>
      {adding ? (
        <form className="row" style={{ marginTop: 10 }} onSubmit={e => { e.preventDefault(); saveNew(); }}>
          <input className="input" style={{ flex: '0 0 170px' }} placeholder="標籤 (例如: 地盤B)" value={f.label} onChange={e => setF({ ...f, label: e.target.value })} />
          <input className="input" style={{ flex: 1 }} placeholder="完整地址" value={f.address} onChange={e => setF({ ...f, address: e.target.value })}
 />
          <button type="submit" className="btn primary">💾 儲存地址</button>
        </form>
      ) : null}
    </div>
  );
}

/* ---------- 明細項目編輯: 主項 + 細項 ---------- */
export function ItemsEditor({ items, setItems, showCost }) {
  const [pick, setPick] = useState(false); // 📚 目錄揀選器
  const update = (i, k, v) => {
    const list = [...items];
    list[i] = { ...list[i], [k]: v };
    if (k === 'qty' || k === 'unit_price') {
      list[i].amount = Math.round(((Number(list[i].qty) || 0) * (Number(list[i].unit_price) || 0)) * 100) / 100;
    }
    setItems(list);
  };
  const amount = it => Math.round(((Number(it.qty) || 0) * (Number(it.unit_price) || 0)) * 100) / 100;

  // 分組: 細項 = 描述以 "-" 開頭, 跟屬上一個主項
  const groups = [];
  items.forEach((it, i) => {
    const isSub = (it.description || '').trim().startsWith('-');
    if (isSub && groups.length > 0) groups[groups.length - 1].subs.push(i);
    else groups.push({ main: i, subs: [] });
  });

  const addMain = () => setItems([...items, { item_id: null, code: '', description: '', qty: 1, unit: '項', unit_price: 0, cost_price: 0 }]);
  const removeGroup = g => {
    const idx = [g.main, ...g.subs].sort((a, b) => b - a);
    const list = [...items];
    idx.forEach(i => list.splice(i, 1));
    setItems(list);
  };
  const removeSub = i => setItems(items.filter((_, x) => x !== i));
  const addSub = (g, text) => {
    if (!text.trim()) return;
    const insertAt = g.subs.length ? g.subs[g.subs.length - 1] + 1 : g.main + 1;
    const list = [...items];
    list.splice(insertAt, 0, { item_id: null, code: '', description: '- ' + text.trim().replace(/^-\s*/, ''), qty: 0, unit: '', unit_price: 0, cost_price: 0 });
    setItems(list);
  };
  const setSub = (i, text) => update(i, 'description', '- ' + text.replace(/^-\s*/, ''));

  const subtotal = groups.reduce((s, g) => s + amount(items[g.main]), 0);

  return (
    <div>
      {groups.map((g, n) => {
        const m = items[g.main];
        const margin = showCost && (Number(m.unit_price) || 0) > 0
          ? Math.round(((Number(m.unit_price) || 0) - (Number(m.cost_price) || 0)) / (Number(m.unit_price) || 0) * 100) : null;
        return (
          <div className="item-group" key={g.main}>
            <div className="ig-head">
              <span className="ig-no">項目 {n + 1}</span>
              {g.subs.length ? <span className="ig-cnt">{g.subs.length} 細項</span> : null}
              <span style={{ flex: 1 }} />
              <span className="ig-amt">HK$ {money(amount(m))}</span>
              <button type="button" className="del-btn" onClick={() => removeGroup(g)}>✕</button>
            </div>
            <input className="input ig-desc" value={m.description || ''} onChange={e => update(g.main, 'description', e.target.value)}
              placeholder="主項描述 (例如: Supply and install FCU c/w:-)" />
            <div className="ig-row">
              <div className="field"><label>數量</label><input className="input num-input" type="number" min="0" step="any" value={m.qty} onChange={e => update(g.main, 'qty', e.target.value)} /></div>
              <div className="field"><label>單位</label><input className="input" value={m.unit || ''} onChange={e => update(g.main, 'unit', e.target.value)} /></div>
              <div className="field"><label>單價</label><input className="input num-input" type="number" min="0" step="0.01" value={m.unit_price} onChange={e => update(g.main, 'unit_price', e.target.value)} /></div>
              {showCost ? (
                <div className="field"><label>成本價</label><input className="input num-input" type="number" min="0" step="0.01" value={m.cost_price || 0} onChange={e => update(g.main, 'cost_price', e.target.value)} /></div>
              ) : null}
              {showCost ? <div className="field"><label>毛利</label><div className="ig-margin" style={{ color: margin === null ? 'var(--ink-3)' : margin < 0 ? 'var(--coral)' : 'var(--mint)' }}>{margin !== null ? margin + '%' : '—'}</div></div> : null}
            </div>
            <div className="ig-subs">
              {g.subs.map(si => (
                <div className="ig-sub" key={si}>
                  <span className="ig-sub-dot">▪</span>
                  <input className="input ig-sub-input" value={(items[si].description || '').replace(/^-\s*/, '')}
                    onChange={e => setSub(si, e.target.value)} placeholder="細項" />
                  <button type="button" className="del-btn" onClick={() => removeSub(si)}>✕</button>
                </div>
              ))}
              <SubAdder key={'add' + g.main} onAdd={t => addSub(g, t)} />
            </div>
          </div>
        );
      })}
      {groups.length === 0 ? (
        <div className="muted" style={{ textAlign: 'center', padding: 22 }}>未有項目 — 撳「➕ 新增項目」或「📚 目錄」開始</div>
      ) : null}
      <div className="row end" style={{ gap: 14, marginTop: 12 }}>
        <button type="button" className="btn sm" onClick={() => setPick(true)}>📚 從目錄加入</button>
        <button type="button" className="btn sm primary" onClick={addMain}>➕ 新增項目</button>
        <div className="strong" style={{ fontSize: 15 }}>小計: HK$ {money(subtotal)}</div>
      </div>
      <div className="muted" style={{ marginTop: 8 }}>💡 每個項目可加細項 (PDF 會縮排顯示喺主項下面)。主項填數量+單價會自動編號 1/ 2/ 3/。</div>
      {pick && <CatalogPicker onClose={() => setPick(false)} onPick={ci => {
        setItems([...items, { item_id: ci.id, code: ci.code || '', description: ci.description, qty: 1, unit: ci.unit || '項', unit_price: Number(ci.unit_price) || 0, cost_price: 0 }]);
      }} />}
    </div>
  );
}

/* 📚 項目目錄揀選器 — 搜尋 + 點擊加入 */
export function CatalogPicker({ onClose, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get('/items' + (q ? '?q=' + encodeURIComponent(q) : '')).then(setRows).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Modal size="xl" onClose={onClose} icon="📚" title="項目目錄 — 搜尋然後點擊加入"
      footer={<>
        <span className="muted">{picked.length ? `已加入 ${picked.length} 項 ✔` : '可以連續加多個'}</span>
        <span style={{ flex: 1 }} />
        <button className="btn primary" onClick={onClose}>完成</button>
      </>}>
      <div className="search-box" style={{ marginBottom: 12 }}>
        <input className="input" autoFocus placeholder="🔍 搜尋項目描述或編號 (中/英)…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      {rows === null ? <Loading /> : rows.length === 0 ? <Empty icon="📚" text={q ? '搵唔到相符項目' : '目錄係空'} /> : (
        <div className="tbl-wrap" style={{ maxHeight: 480, overflowY: 'auto' }}>
          <table className="tbl">
            <thead><tr><th>描述</th><th>編號</th><th>單位</th><th className="num">單價</th><th></th></tr></thead>
            <tbody>
              {rows.map(ci => (
                <tr key={ci.id} className={'clickable' + (picked.includes(ci.id) ? ' picked-row' : '')} onClick={() => { onPick(ci); setPicked(p => [...p, ci.id]); }}>
                  <td className="strong" style={{ maxWidth: 420 }}>{(ci.description || '').startsWith('-') ? '　' : ''}{ci.description}</td>
                  <td className="sub">{ci.code || '—'}</td>
                  <td>{ci.unit}</td>
                  <td className="num">{Number(ci.unit_price) ? money(ci.unit_price) : '—'}</td>
                  <td>{picked.includes(ci.id) ? <span className="st st-green">已加入</span> : <span className="btn sm primary">➕ 加入</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function SubAdder({ onAdd }) {
  const [t, setT] = useState("");
  return (
    <form className="ig-sub" onSubmit={e => { e.preventDefault(); if (t.trim()) { onAdd(t); setT(""); } }}>
      <span className="ig-sub-dot">＋</span>
      <input className="input ig-sub-input" placeholder="加細項 (例如 Hanger Support) — 按 Enter" value={t} onChange={e => setT(e.target.value)} />
      <button type="submit" className="btn sm">加入</button>
    </form>
  );
}
